import { useState, type FormEvent } from 'react';

const FORM_ENDPOINT = 'https://readdy.ai/api/form/db6faccbe24agvuu3j20';

interface FormResponse {
  code?: string;
  message?: string;
  meta?: { message?: string; detail?: unknown };
}

export default function FeedbackForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') ?? '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      if (typeof value === 'string' && value.trim() === '') return;
      payload.append(key, String(value));
    });

    setStatus('submitting');
    setFormError('');

    try {
      const response = await fetch(FORM_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();

      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ??
        parsed?.message ??
        (typeof parsed?.meta?.detail === 'string' ? parsed.meta.detail : '') ??
        responseText;

      const code = parsed?.code;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (!response.ok || code !== 'OK' || isSpam) {
        setFormError(
          typeof serverMsg === 'string' && serverMsg
            ? serverMsg
            : '提交失败，请稍后重试',
        );
        setStatus('error');
        return;
      }

      setStatus('success');
      form.reset();
    } catch {
      setFormError('网络异常，提交失败，请稍后重试');
      setStatus('error');
    }
  };

  const inputClass =
    'h-11 w-full rounded-md border border-background-300 bg-background-50 px-4 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400';

  return (
    <form
      id="feedback-form"
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor="fb-name" className="mb-2 block text-sm font-medium text-foreground-800">
            您的称呼
          </label>
          <input
            id="fb-name"
            name="name"
            type="text"
            required
            placeholder="请输入您的称呼"
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor="fb-email" className="mb-2 block text-sm font-medium text-foreground-800">
            联系邮箱
          </label>
          <input
            id="fb-email"
            name="email"
            type="email"
            required
            placeholder="用于接收我们的回复"
            className={inputClass}
          />
        </div>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-category" className="mb-2 block text-sm font-medium text-foreground-800">
          反馈类型
        </label>
        <select id="fb-category" name="category" className={`${inputClass} cursor-pointer`}>
          <option value="内容纠错">内容纠错</option>
          <option value="功能建议">功能建议</option>
          <option value="影视收录">影视收录申请</option>
          <option value="其他反馈">其他反馈</option>
        </select>
      </div>

      <div className="mt-4">
        <label htmlFor="fb-message" className="mb-2 block text-sm font-medium text-foreground-800">
          反馈内容
        </label>
        <textarea
          id="fb-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你遇到的问题或建议（最多 500 字）"
          className="w-full rounded-md border border-background-300 bg-background-50 px-4 py-3 text-sm text-foreground-950 outline-none transition-colors focus:border-primary-400 placeholder:text-foreground-400"
        ></textarea>
      </div>

      <div className="form-field-extra" aria-hidden="true">
        <label htmlFor="fb-website-alt">请勿填写此栏</label>
        <input
          id="fb-website-alt"
          name="website_alt"
          type="text"
          tabIndex={-1}
          autoComplete="off"
          readOnly
        />
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-4">
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="flex items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-7 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 cursor-pointer dark:text-foreground-950"
        >
          <i className="ri-send-plane-line" aria-hidden="true"></i>
          {status === 'submitting' ? '提交中...' : '提交反馈'}
        </button>
        <p className="text-xs text-foreground-500">
          提交即表示你同意我们仅将该信息用于本网站的内容改进。
        </p>
      </div>

      {status === 'success' ? (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-primary-700">
          <i className="ri-checkbox-circle-line text-lg" aria-hidden="true"></i>
          反馈提交成功，感谢你的建议，我们会尽快查看。
        </div>
      ) : null}

      {status === 'error' ? (
        <div className="mt-4 flex items-center gap-2 rounded-md border border-accent-300 bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <i className="ri-error-warning-line text-lg" aria-hidden="true"></i>
          {formError || '提交失败，请稍后重试'}
        </div>
      ) : null}
    </form>
  );
}